import { useEffect } from 'react';
import { useDiagramContext } from '../Context/DiagramContext';
import { useBpmnInitialization } from './useBpmnInitialisation';
import { useDiagramEventListeners } from './useDiagramEventListner';
import { useDiagramBadges } from './useDiagramBadges';
import { useDiagramZoom } from './useDiagramZoom';

export const useDiagramLifecycle = () => {
  const {
    diagramData,
    aiOpportunityData,
    isEditPermission,
    isProcessDiagram,
    bpmnModelerRef,
    containerRef,
    setLoading,
    setError,
    lastSavedXmlRef,
    prevDiagramData,
    setPrevDiagramData,
  } = useDiagramContext();

  const { initializeBpmn } = useBpmnInitialization(isEditPermission, isProcessDiagram);
  const { attachEventListeners, detachEventListeners } = useDiagramEventListeners();
  const { updateDiagramBadges } = useDiagramBadges();
  const { handleInitialFitView } = useDiagramZoom();

  useEffect(() => {
    // Initialize window property
    (window as any)._aiOpportunityData = [...aiOpportunityData];

    if (!diagramData?.diagram?.xml) {
      return;
    }

    const xmlContent = diagramData.diagram.xml;
    setLoading(true);
    setError(null);

    // Cleanup existing instance
    if (bpmnModelerRef.current) {
      bpmnModelerRef.current.destroy();
      bpmnModelerRef.current = undefined;
    }

    if (containerRef.current) {
      // Initialize BPMN instance
      bpmnModelerRef.current = initializeBpmn(containerRef.current);
      (window as any).bpmnModelerInstance = bpmnModelerRef.current;

      // Store initial state
      if (lastSavedXmlRef && 'current' in lastSavedXmlRef) {
        (lastSavedXmlRef as any).current = xmlContent;
      }

      if (!prevDiagramData || !Object.keys(prevDiagramData)?.length) {
        setPrevDiagramData?.(diagramData?.diagram?.data);
      }

      // Import XML
      bpmnModelerRef.current
        .importXML(xmlContent)
        .then(({ warnings }: any) => {
          if (warnings?.length) {
            console.warn('BPMN import warnings:', warnings);
          }

          // Fit to viewport after import
          handleInitialFitView();

          // Attach event listeners
          attachEventListeners();

          // Update badges
          setTimeout(() => {
            updateDiagramBadges();
          }, 0);

          setLoading(false);
        })
        .catch((err: Error) => {
          console.error('Failed to render BPMN diagram:', err);
          setLoading(false);
        });
    }

    // Cleanup
    return () => {
      detachEventListeners();
      if (bpmnModelerRef.current) {
        bpmnModelerRef.current.destroy();
        bpmnModelerRef.current = undefined;
        delete (window as any).bpmnModelerInstance;
      }
    };
  }, [diagramData?.diagram?.xml, aiOpportunityData.length, isEditPermission]);
};
